import axios from 'axios'
import { ExternalLink } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Pulse, Segmented } from '@/components/activity/activity-panel'
import { config as envConfig } from '@/configs/env.config'
import { cn } from '@/lib/utils'
import type { AuditLogEvent } from '@/models/IActivity'
import { useQueryGetUserAuditLogs, USER_AUDIT_LOG_LIMIT } from '@/services'
import {
  AuditCategory,
  formatAuditTime,
  getAuditCategory,
  getAuditTitle
} from '@/utils/activity'

const CATEGORY_OPTIONS: { value: AuditCategory; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'auth', label: 'Sign-ins & org switches' },
  { value: 'changes', label: 'Changes' }
]

const getErrorText = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const code = (error.response?.data as { code?: string } | undefined)?.code

    if (code === 'INSUFFICIENT_TIER')
      return 'The audit log API only serves organizations on a pricing tier with audit logs.'
    if (error.response?.status === 403)
      return 'The audit log API refused the request (403).'
    if (!error.response)
      return 'The audit log API could not be reached (network / CORS).'
  }

  return 'The audit log could not be loaded.'
}

const Chip = ({
  children,
  tone = 'default',
  mono
}: {
  children: React.ReactNode
  tone?: 'default' | 'auth' | 'internal'
  mono?: boolean
}) => (
  <span
    className={cn(
      'inline-flex max-w-full truncate rounded px-1.5 py-0.5 text-[11px]',
      mono && 'font-mono',
      tone === 'auth' && 'bg-emerald-500/15 text-emerald-400',
      tone === 'internal' && 'bg-amber-500/15 text-amber-400',
      tone === 'default' && 'bg-muted/60 text-muted-foreground/90'
    )}
  >
    {children}
  </span>
)

const AuditRow = ({ event }: { event: AuditLogEvent }) => {
  const category = getAuditCategory(event)
  const resource = [event.resource_type, event.resource_id]
    .filter(Boolean)
    .join(' · ')

  return (
    <li className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 border-b border-border/30 py-3 last:border-0">
      <time
        className="text-xs tabular-nums text-muted-foreground/70"
        dateTime={event.timestamp}
        title={new Date(event.timestamp).toLocaleString()}
      >
        {formatAuditTime(event.timestamp)}
      </time>
      <div className="min-w-0 space-y-1">
        <div className="text-sm font-medium">{getAuditTitle(event)}</div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground/70">
          {event.service_name && (
            <Chip tone={category === 'auth' ? 'auth' : 'default'}>
              {event.service_name}
            </Chip>
          )}
          {(event.action || event.event_name) && (
            <Chip mono>{event.action || event.event_name}</Chip>
          )}
          {event.visibility === 'internal' && (
            <Chip tone="internal">epilot staff</Chip>
          )}
          {event.status_code && event.status_code >= 400 && (
            <Chip tone="internal">{event.status_code}</Chip>
          )}
          {resource && <span className="truncate">{resource}</span>}
        </div>
      </div>
    </li>
  )
}

type UserAuditFeedProps = {
  orgId: string
  userId: string
}

/** audit log rows initiated by the user in this organization (incl. internal rows) */
export const UserAuditFeed = ({ orgId, userId }: UserAuditFeedProps) => {
  const [category, setCategory] = useState<AuditCategory>('all')
  const { data, isLoading, error } = useQueryGetUserAuditLogs(orgId, userId)

  const logs = useMemo(
    () =>
      (data?.logs ?? []).filter(
        (event) => category === 'all' || getAuditCategory(event) === category
      ),
    [data, category]
  )

  const portalUrl = envConfig['360_PORTAL_URL']
  const openLink = portalUrl && (
    <a
      className="inline-flex items-center gap-1 text-sm text-secondary underline-offset-4 hover:underline"
      href={`${portalUrl}/app/audit-logs`}
      rel="noreferrer"
      target="_blank"
    >
      Open audit log in 360 <ExternalLink className="h-3.5 w-3.5" />
    </a>
  )

  return (
    <div className="space-y-3 text-foreground">
      <Segmented
        ariaLabel="Audit log filter"
        onChange={setCategory}
        options={CATEGORY_OPTIONS}
        size="sm"
        value={category}
      />
      <p className="text-xs text-muted-foreground/70">
        Audit log filtered to this user in org{' '}
        <span className="font-mono">{orgId}</span> · internal view, includes
        epilot staff actions
        {data && data.logs.length >= USER_AUDIT_LOG_LIMIT
          ? ` · latest ${USER_AUDIT_LOG_LIMIT} events`
          : ''}
      </p>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Pulse className="h-10 w-full" key={i} />
          ))}
        </div>
      ) : error ? (
        <div className="space-y-2 rounded-md bg-muted/30 p-4 text-sm">
          <p>{getErrorText(error)}</p>
          <p className="text-xs text-muted-foreground/70">
            Use &quot;Login as user&quot; and open the audit log in 360 instead
            (filter by the user there).
          </p>
          {openLink}
        </div>
      ) : logs.length === 0 ? (
        <p className="rounded-md bg-muted/30 p-4 text-sm text-muted-foreground/70">
          No audit log entries for this user
          {category === 'all' ? '' : ' in this category'}.
        </p>
      ) : (
        <ol>
          {logs.map((event) => (
            <AuditRow event={event} key={event.id} />
          ))}
        </ol>
      )}

      {!error && openLink}
    </div>
  )
}
